// Click-to-edit on the published page. Out of edit mode this renders exactly
// what a visitor sees; in edit mode a click swaps the block for its markdown
// source in place, so the scripture around it never moves.
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Prose } from "./Prose";

interface Props {
  value: string;
  onChange: (value: string) => void;
  editing: boolean;
  placeholder: string;
  /** Headings and titles: one line, rendered without a paragraph wrapper. */
  inline?: boolean;
  /** Reference matter, such as a citation: no glossary words marked. */
  plain?: boolean;
  /** How the value reads when it is more than markdown — a post, with the passages it quotes. */
  render?: (value: string) => ReactNode;
  className?: string;
  label: string;
}

export function Editable({ value, onChange, editing, placeholder, inline, plain, render, className = "", label }: Props) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value);
  const area = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (!editing) setOpen(false);
  }, [editing]);
  // Grow with the text so the block keeps the height it will have on the page.
  useEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [open, text]);

  if (open) {
    const commit = () => {
      setOpen(false);
      if (text !== value) onChange(text);
    };
    const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Escape") {
        setText(value);
        setOpen(false);
      } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        commit();
      } else if (e.key === "Enter" && inline) {
        e.preventDefault();
        commit();
      }
    };
    return (
      <textarea
        ref={area}
        autoFocus
        aria-label={label}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        className={`block w-full resize-none rounded-md border border-accent bg-paper-2 px-3 py-2 focus-visible:outline-none ${className}`}
        rows={1}
      />
    );
  }

  const empty = !value.trim();
  if (!editing) {
    if (empty) return null;
    return render ? render(value) : <Prose md={value} inline={inline} plain={plain} className={className} />;
  }
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`${label} — click to edit`}
      onClick={() => setOpen(true)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen(true))}
      className={`-mx-3 cursor-text rounded-md px-3 py-0.5 ring-1 ring-transparent hover:bg-paper-2 hover:ring-rule ${className}`}
    >
      {empty ? (
        <span className="text-ink-3 italic">{placeholder}</span>
      ) : render ? (
        render(value)
      ) : (
        <Prose md={value} inline={inline} plain={plain} />
      )}
    </div>
  );
}
